import React from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TextField, Select, MenuItem, Box
} from "@mui/material";
import { format } from "date-fns";

const SCORES_1_7 = ["1", "2", "3", "4", "5", "6", "7"];
const EXPERT_ADJUSTMENT_OPTIONS = ["оставить без изменений", "улучш.на 0,5 балла", "ухудш.на 0,5 балла"];
const DEBT_SERVICE_OPTIONS = ["хорошее", "среднее", "плохое"];
const YES_NO_OPTIONS = ["да", "нет"];

const cellBase = {
  padding: "8px 20px",
  fontSize: "13px",
  lineHeight: "16px",
  letterSpacing: "0.0075em",
  whiteSpace: "nowrap",
};

const headerCell = {
  ...cellBase,
  backgroundColor: "#F3F5F7",
  color: "#54606F",
  fontWeight: 600,
  textAlign: "center",
};

const labelCell = {
  ...cellBase,
  backgroundColor: "#fff",
  minWidth: "220px",
  fontWeight: 500,
  color: "#46515D",
};

const readonlyCell = {
  ...cellBase,
  backgroundColor: "#F9F9F9",
  textAlign: "center",
  color: "#54606F",
};

const sectionHeaderCell = {
  ...cellBase,
  backgroundColor: "#F3F5F7",
  fontWeight: 700,
  fontSize: "15px",
  color: "#354659",
  letterSpacing: "0.0075em",
};

const scoreLabelCell = {
  ...cellBase,
  backgroundColor: "#F3F5F7",
  minWidth: "220px",
  fontWeight: 700,
  fontSize: "15px",
  color: "#354659",
};

const scoreReadonlyCell = {
  ...readonlyCell,
  backgroundColor: "#F3F5F7",
  fontWeight: 700,
  fontSize: "15px",
  color: "#354659",
};

const inputStyle = {
  "& .MuiInputBase-input": {
    padding: "6px 8px",
    fontSize: "13px",
    lineHeight: "16px",
    textAlign: "center",
    color: "#1A1E24",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6FA4F2",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6FA4F2",
  },
  backgroundColor: "#fff",
  borderRadius: "4px",
};

const selectStyle = {
  fontSize: "13px",
  width: "100%",
  "& .MuiSelect-select": {
    padding: "6px 28px 6px 8px",
    fontSize: "13px",
    lineHeight: "16px",
    color: "#1A1E24",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6FA4F2",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6FA4F2",
  },
  backgroundColor: "#fff",
  borderRadius: "4px",
};

const ReadonlyCell = ({ value, bold }) => (
  <TableCell sx={bold ? scoreReadonlyCell : readonlyCell}>{value ?? ""}</TableCell>
);

const inputCell = {
  ...cellBase,
  padding: "4px 8px",
  backgroundColor: "#fff",
};

const NumberInputCell = ({ value, onChange }) => (
  <TableCell sx={{ ...inputCell, textAlign: "center" }}>
    <TextField
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      variant="outlined"
      size="small"
      fullWidth
      sx={inputStyle}
    />
  </TableCell>
);

const SelectCell = ({ value, options, onChange }) => (
  <TableCell sx={{ ...inputCell, textAlign: "center" }}>
    <Select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      size="small"
      sx={selectStyle}
      displayEmpty
    >
      <MenuItem value=""><em>—</em></MenuItem>
      {options.map((o) => (
        <MenuItem key={o} value={o}>{o}</MenuItem>
      ))}
    </Select>
  </TableCell>
);

const TextInputCell = ({ value, onChange }) => (
  <TableCell sx={inputCell}>
    <TextField
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      size="small"
      fullWidth
      sx={inputStyle}
    />
  </TableCell>
);

const SpanningTextCell = ({ value, colSpan, onChange }) => (
  <TableCell colSpan={colSpan} sx={inputCell}>
    <TextField
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      variant="outlined"
      size="small"
      fullWidth
      sx={{ ...inputStyle, "& .MuiInputBase-input": { ...inputStyle["& .MuiInputBase-input"], textAlign: "left" } }}
    />
  </TableCell>
);

const SectionRow = ({ label, colSpan }) => (
  <TableRow>
    <TableCell colSpan={colSpan + 1} sx={sectionHeaderCell}>{label}</TableCell>
  </TableRow>
);

const formatDate = (dateStr) => {
  try {
    return format(new Date(dateStr), "dd.MM.yyyy");
  } catch {
    return dateStr || "";
  }
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `${+(num * 100).toFixed(2)}%`;
};

const RKKQuartersTable = ({ quarters, startOffset, ratingData, onUpdate }) => {
  const qCount = quarters.length;

  const updateQuarter = (qIdx, path, value) => {
    onUpdate(`rating.quarters.${startOffset + qIdx}.${path}`, value);
  };

  const updateRating = (path, value) => {
    onUpdate(`rating.${path}`, value);
  };

  return (
    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #CED7EC" }}>
      <Table size="small" sx={{
        tableLayout: "auto",
        borderCollapse: "collapse",
        "& .MuiTableCell-root": {
          borderBottom: "1px solid #B8C4D2",
          borderRight: "1px solid #CED7EC",
        },
      }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCell, minWidth: "220px" }}>Показатель</TableCell>
            {quarters.map((q, i) => (
              <TableCell key={i} sx={{ ...headerCell, minWidth: "160px" }}>
                {formatDate(q.displayDate)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>

          {/* ── Данные для расчета ── */}
          <SectionRow label="Данные для расчета" colSpan={qCount} />
          <TableRow>
            <TableCell sx={labelCell}>Совокупный лимит кредитования (тыс. руб.)</TableCell>
            {quarters.map((q, i) => (
              <NumberInputCell
                key={i}
                value={q.calculationData?.totalCreditLimit}
                onChange={(v) => updateQuarter(i, "calculationData.totalCreditLimit", v)}
              />
            ))}
          </TableRow>

          {/* ── Нефинансовые факторы ── */}
          <SectionRow label="Нефинансовые факторы" colSpan={qCount} />
          {[
            { label: "Положение отрасли", path: "nonFinancialFactors.industryPosition" },
            { label: "Положение компании в отрасли", path: "nonFinancialFactors.companyPositionInIndustry" },
            { label: "Качество менеджмента", path: "nonFinancialFactors.managementQuality" },
            { label: "Диверсификация клиентской базы", path: "nonFinancialFactors.clientBaseDiversification" },
            { label: "Деловая репутация, кредитная история", path: "nonFinancialFactors.businessReputation" },
          ].map(({ label, path }) => (
            <TableRow key={path}>
              <TableCell sx={labelCell}>{label}</TableCell>
              {quarters.map((q, i) => {
                const keys = path.split(".");
                const val = keys.reduce((obj, k) => obj?.[k], q);
                return (
                  <SelectCell
                    key={i}
                    value={val}
                    options={SCORES_1_7}
                    onChange={(v) => updateQuarter(i, path, v)}
                  />
                );
              })}
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={scoreLabelCell}>Балл</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} bold value={q.nonFinancialFactors?.totalScore} />
            ))}
          </TableRow>

          {/* ── Финансовые показатели ── */}
          <SectionRow label="Финансовые показатели" colSpan={qCount} />
          {[
            { label: "Коэффициент текущей ликвидности", path: "financialIndicators.currentLiquidityRatio" },
            { label: "Коэффициент финансовой устойчивости", path: "financialIndicators.financialStabilityRatio" },
            { label: "Рентабельность продаж", path: "financialIndicators.salesProfitability" },
            { label: "Рентабельность активов", path: "financialIndicators.assetProfitability" },
            { label: "Оборот оборотных активов, мес.", path: "financialIndicators.currentAssetsTurnover" },
            { label: "Выручка к кредитам компании", path: "financialIndicators.revenueToCreditRatio" },
            { label: "Кредиты Банка к активам компании", path: "financialIndicators.bankCreditToAssetRatio" },
            { label: "Динамика продаж", path: "financialIndicators.salesDynamics" },
          ].map(({ label, path }) => (
            <TableRow key={path}>
              <TableCell sx={labelCell}>{label}</TableCell>
              {quarters.map((q, i) => {
                const keys = path.split(".");
                const val = keys.reduce((obj, k) => obj?.[k], q);
                return <ReadonlyCell key={i} value={val} />;
              })}
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={labelCell}>Корректировка кредитного эксперта</TableCell>
            {quarters.map((q, i) => (
              <SelectCell
                key={i}
                value={q.financialIndicators?.expertAdjustment}
                options={EXPERT_ADJUSTMENT_OPTIONS}
                onChange={(v) => updateQuarter(i, "financialIndicators.expertAdjustment", v)}
              />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={scoreLabelCell}>Балл</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} bold value={q.financialIndicators?.totalScore} />
            ))}
          </TableRow>

          {/* ── Итоги ── */}
          <TableRow>
            <TableCell sx={scoreLabelCell}>Итоговый балл</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} bold value={q.totalScore} />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Финансовое положение</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} value={q.financialCondition} />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Обслуживание долга</TableCell>
            {quarters.map((q, i) => (
              <SelectCell
                key={i}
                value={q.debtService}
                options={DEBT_SERVICE_OPTIONS}
                onChange={(v) => updateQuarter(i, "debtService", v)}
              />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Категория качества</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} value={q.qualityCategory} />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Расчетный резерв</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} value={formatPercent(q.estimatedReserve)} />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Комментарии к итоговым показателям</TableCell>
            <SpanningTextCell
              colSpan={qCount}
              value={ratingData?.commentsToFinalIndicators}
              onChange={(v) => updateRating("commentsToFinalIndicators", v)}
            />
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Корректировка иными существ. факторами</TableCell>
            {quarters.map((q, i) => (
              <SelectCell
                key={i}
                value={q.adjustmentByOtherFactors}
                options={YES_NO_OPTIONS}
                onChange={(v) => updateQuarter(i, "adjustmentByOtherFactors", v)}
              />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Корректировка п.3.12, 3.13, 3.14 по 254-П</TableCell>
            {quarters.map((q, i) => (
              <SelectCell
                key={i}
                value={q.adjustmentBy254P}
                options={YES_NO_OPTIONS}
                onChange={(v) => updateQuarter(i, "adjustmentBy254P", v)}
              />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Комментарии к корректировкам</TableCell>
            <SpanningTextCell
              colSpan={qCount}
              value={ratingData?.commentsToAdjustments}
              onChange={(v) => updateRating("commentsToAdjustments", v)}
            />
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Снижение резерва залогом</TableCell>
            {quarters.map((q, i) => (
              <SelectCell
                key={i}
                value={q.reserveReductionByCollateral}
                options={YES_NO_OPTIONS}
                onChange={(v) => updateQuarter(i, "reserveReductionByCollateral", v)}
              />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Итоговая категория качества</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} value={q.finalQualityCategory} />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Итоговый резерв</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} value={formatPercent(q.finalReserve)} />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Рейтинг по внутрен. оценке Банка</TableCell>
            {quarters.map((q, i) => (
              <TextInputCell
                key={i}
                value={q.internalBankRating}
                onChange={(v) => updateQuarter(i, "internalBankRating", v)}
              />
            ))}
          </TableRow>

          {/* ── Рейтинг заемщика для МСФО ── */}
          <SectionRow label="Рейтинг заемщика для МСФО" colSpan={qCount} />
          <TableRow>
            <TableCell sx={labelCell}>Финансовое положение</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} value={q.ifrsRating?.financialCondition} />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Кредитная история</TableCell>
            {quarters.map((q, i) => (
              <TextInputCell
                key={i}
                value={q.ifrsRating?.creditHistory}
                onChange={(v) => updateQuarter(i, "ifrsRating.creditHistory", v)}
              />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={scoreLabelCell}>Общий итоговый балл</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} bold value={q.totalScoreWithCreditHistory} />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Рейтинг (МСФО)</TableCell>
            {quarters.map((q, i) => (
              <ReadonlyCell key={i} value={q.ifrsRating?.rating} />
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={labelCell}>Комментарии к итоговым показателям</TableCell>
            <SpanningTextCell
              colSpan={qCount}
              value={ratingData?.commentsToGeneralFinalIndicators}
              onChange={(v) => updateRating("commentsToGeneralFinalIndicators", v)}
            />
          </TableRow>

        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RKKQuartersTable;
